import {
  CountrySelect,
  InputDatePicker,
  ResidentSelect
} from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { InputParsedNumber } from '@shared/components'
import { Col, Form, FormListFieldData, Input, Row } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'

import { EditPersonsWrapper } from './EditPersonsWrapper'
import { Person } from './types'

type Props = {
  personsKey?: string
  showAddAction?: boolean
  isEditable?: boolean
  showDeleteAction?: (name: number, person?: Person) => boolean
  minAge?: number
  short?: boolean
  resident?: boolean
  isDisabledBirthDate?: boolean
  showExperience?: boolean
  showResident?: boolean
  collapseTitle?: (field: FormListFieldData) => React.ReactNode

  onChangeBirthDate?: (value?: Dayjs) => void
  onChangeExperience?: (value?: number | null) => void
}

export const EditPersonsFields = ({
  personsKey = 'persons',
  isEditable,
  minAge,
  short,
  resident,
  isDisabledBirthDate,
  showExperience,
  showResident,
  onChangeBirthDate,
  onChangeExperience,
  ...props
}: Props) => {
  const { t } = useTranslation()
  const { fullName, required, minLength, personalCode, passportSeriesNumber } =
    useValidationsField()

  return (
    <EditPersonsWrapper personsKey={personsKey} {...props}>
      {(field, persons) => {
        const personResident = persons?.[field.name]?.resident
        const personCountry = persons?.[field.name]?.country

        return (
          <Row gutter={12}>
            <Col xs={24} lg={short ? 12 : 8}>
              <Form.Item
                label={t('last_name')}
                name={[field.name, 'last_name']}
                rules={[required, fullName]}
              >
                <Input disabled={isEditable} placeholder={t('last_name')} />
              </Form.Item>
            </Col>

            <Col xs={24} lg={short ? 12 : 8}>
              <Form.Item
                label={t('first_name')}
                name={[field.name, 'first_name']}
                rules={[required, fullName]}
              >
                <Input disabled={isEditable} placeholder={t('first_name')} />
              </Form.Item>
            </Col>

            {!!minAge && (
              <Col xs={24} lg={short ? 12 : 8}>
                <Form.Item
                  label={t('date_of_birth')}
                  name={[field.name, 'date_of_birth']}
                  rules={[required]}
                >
                  <InputDatePicker
                    disabled={isDisabledBirthDate}
                    maxDate={dayjs().subtract(minAge, 'year')}
                    onChange={onChangeBirthDate}
                  />
                </Form.Item>
              </Col>
            )}

            {showExperience && (
              <Col xs={24} lg={12}>
                <Form.Item
                  label={t('driving_internship')}
                  name={[field.name, 'experience']}
                  rules={[required]}
                >
                  <InputParsedNumber
                    className="w-full"
                    placeholder={t('driving_internship')}
                    min={1}
                    suffix={t('years')}
                    onChange={onChangeExperience}
                  />
                </Form.Item>
              </Col>
            )}

            <Col xs={24} lg={showExperience ? 24 : 12}>
              <Form.Item
                label={t('identification_number')}
                name={[field.name, 'identification_number']}
                rules={[
                  required,
                  (typeof resident === 'boolean' ? resident : personResident)
                    ? personalCode
                    : minLength,
                  {
                    validator: (_, value) => {
                      const duplicateExists =
                        persons?.filter((person) => {
                          if (!person?.identification_number) return false
                          const otherIdnp = String(
                            person?.identification_number
                          ).trim()

                          return otherIdnp === value
                        }).length > 1

                      if (duplicateExists)
                        return Promise.reject(t('duplicate_idnp_error'))

                      return Promise.resolve()
                    }
                  }
                ]}
              >
                <Input
                  disabled={isEditable}
                  placeholder={t('identification_number')}
                />
              </Form.Item>
            </Col>

            {!short && (
              <Col xs={24} lg={12}>
                <Form.Item
                  label={t('passport_series')}
                  name={[field.name, 'document']}
                  rules={[required, passportSeriesNumber]}
                >
                  <Input
                    disabled={isEditable}
                    placeholder={t('passport_series')}
                  />
                </Form.Item>
              </Col>
            )}

            {showResident && (
              <Col span={24}>
                <Form.Item name={[field.name, 'resident']} label={t('status')}>
                  <ResidentSelect
                    placeholder={t('status')}
                    disabled={isEditable}
                    countryCode={personCountry}
                  />
                </Form.Item>
              </Col>
            )}

            {showResident && !personResident && (
              <Col span={24}>
                <Form.Item
                  name={[field.name, 'country']}
                  label={t('country_of_residence')}
                  rules={[required]}
                >
                  <CountrySelect
                    placeholder={t('country_of_residence')}
                    resident={personResident}
                    disabled={isEditable}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        )
      }}
    </EditPersonsWrapper>
  )
}
